import { ThemeProvider } from "./context/ThemeContext";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import Login from "./pages/Login/Login";
import { Container, CssBaseline } from "@mui/material";
import Logout from "./components/Login/Logout/Logout";
import CreateBusinessAccount from "./pages/CreateBusinessAccount/CreateBusinessAccount";
import SessionManagement from "./components/SessionManagement/SessionManagement";
import MainNavigation from "./components/MainNavigation/MainNavigation";
import { PopupProvider } from "./context/LoginPopupContext";
import Dashboard from "./components/Dashboard/Dashboard";
import DashboardMobile from "./components/DashboardMobile/DashboardMobile";
import { AuthProvider } from "./context/CheckApiKeyContext";
import TwoFactorSetup from "./pages/MFAConfig/MFAConfig";
import ManageSubscriber from "./pages/ManageSubscriber/ManageSubscriber";
import { licenseKey } from "./devextreme-license";
import config from "devextreme/core/config";
import GlobalUserEdit from "./pages/GlobalUserEdit/GlobalUserEdit";
import InviteSubscriberPage from "./pages/InviteSubscriberPage/InviteSubscriberPage";
import BankFeedsConnected from "./pages/BankFeedsConntected/BankFeedsConntected";
import { PixelProvider } from "./context/PixelContext";
import CreateDemoBusinessFile from "./pages/CreateDemoBusinessFile/CreateDemoBusinessFile";
import TawkToChat from "./components/TawkToChat/TawkToChat";
import Footer from "./components/Footer/Footer";
import { ErrorModalProvider } from "./context/ErrorModalContext";
import { useErrorModalInterceptor } from "./hooks/useErrorModalInterceptor";
import TestErrors from "./pages/TestErrors/TestErrors";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import AuthRedirect from "./components/AuthRedirect/AuthRedirect";
import { initializeSessionManager } from "./utils/session-manager";
import { isAuthShellRoute } from "./utils/auth-shell-routes";
import { useEffect } from "react";
import "./lib/api-interceptor";

const getRecaptchaSiteKey = () => {
  if (typeof window === "undefined") return null;
  if (window.location.origin.includes(".exx.")) return process.env.REACT_APP_SITE_KEY_EXX;
  if (window.location.origin.includes(".ssx.")) return process.env.REACT_APP_SITE_KEY_SSX;
  if (window.location.origin.includes("jimssoft.com")) return process.env.REACT_APP_SITE_KEY_JIMSSOFT;
  return process.env.REACT_APP_SITE_KEY_MY_EXACC;
};

const AppContent = () => {
  useErrorModalInterceptor();
  const location = useLocation();
  const showFooter = !isAuthShellRoute(location.pathname);
  
  const prefix = "/:dbId/:lang";

  return (
    <>
      <TawkToChat />
      <CssBaseline />
      <>
                <Routes>
                  <Route
                    path="/"
                    element={
                      (process.env.REACT_APP_USE_SAMPLE_DATA === 'true' || window.location.hostname === 'localhost') ? (
                        <Container>
                          <MainNavigation pageCode="gl-dashboard" />
                          <Dashboard />
                        </Container>
                      ) : (
                        <AuthRedirect>
                          <Login />
                        </AuthRedirect>
                      )
                    }
                  />
                  <Route
                    path={`${prefix}/authorization/validation/:sessionGuid`}
                    element={
                      <>
                        <Container>
                          <SessionManagement />
                        </Container>
                      </>
                    }
                  />

                  <Route
                    path={"/subscriber"}
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-subscriber" />
                          <ManageSubscriber />
                        </Container>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path={"/bank-feeds-connected"}
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-subscriber" />
                          <BankFeedsConnected />
                        </Container>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={"/subscriber/:id"}
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-subscriber" />
                          <ManageSubscriber />
                        </Container>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={"/user-dashboard"}
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-dashboard" />
                          <Dashboard />
                        </Container>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={"/add-member-to-subscriber/:id"}
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-invite-subscriber" />
                          <InviteSubscriberPage />
                        </Container>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={`/mfa-setup`}
                    element={
                      <ProtectedRoute>
                        <TwoFactorSetup />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/create-new-exacc-file"
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-create-business-file" />
                          <CreateBusinessAccount />
                        </Container>
                      </ProtectedRoute>
                    }
                  ></Route>
                  <Route
                    path="/create-new-subscriber"
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-create-subscriber" />
                          <CreateBusinessAccount isCreatingSubscriber={true} />
                        </Container>
                      </ProtectedRoute>
                    }
                  ></Route>
                  <Route
                    path="/create-demo-file"
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-create-demo-file" />
                          <CreateDemoBusinessFile />
                        </Container>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-details"
                    element={
                      <ProtectedRoute>
                        <Container>
                          <MainNavigation pageCode="gl-my-details" />
                          <GlobalUserEdit />
                        </Container>
                      </ProtectedRoute>
                    }
                  ></Route>

                  <Route path="/logout" element={<Logout />}></Route>
                  
                  <Route
                    path="/dev/test-errors"
                    element={
                      <Container>
                        <TestErrors />
                      </Container>
                    }
                  />

                  <Route
                    path={`*`}
                    element={
                      <AuthRedirect>
                        <Login />
                      </AuthRedirect>
                    }
                  />
                </Routes>
      </>
      {showFooter && <Footer />}
    </>
  );
};

const App = () => {
  config({ licenseKey: licenseKey });

  useEffect(() => {
    // Initialize complete session management system
    const cleanup = initializeSessionManager({
      enableDebug: process.env.NODE_ENV === 'development',
      enablePerformanceMonitoring: true,
      exposeDebugTools: process.env.NODE_ENV === 'development'
    });
    
    return cleanup; // Cleanup on unmount
  }, []);

  return (
    <PixelProvider>
      <ThemeProvider>
        <BrowserRouter>
          <PopupProvider>
            <AuthProvider>
              <ErrorModalProvider>
                <GoogleReCaptchaProvider reCaptchaKey={getRecaptchaSiteKey() || ""}>
                  <AppContent />
                </GoogleReCaptchaProvider>
              </ErrorModalProvider>
            </AuthProvider>
          </PopupProvider>
        </BrowserRouter>
      </ThemeProvider>
    </PixelProvider>
  );
};

export default App;
